import UserFingerprintApi from '@/api/UserFingerprintApi';
import { getArmadoApp, initSentry } from '@/app';
import router from '@/router/index.router';
import * as FingerprintJS from '@fingerprintjs/fingerprintjs-pro';
import * as Sentry from '@sentry/vue';
import { Thumbmark } from '@thumbmarkjs/thumbmarkjs';

interface ExtendedVisitorData extends VisitorData {
	stytchTelemetryId?: string
	thumbmarkFingerprint?: string
}

class FingerprintService {

	private readonly stytchPublicToken = import.meta.env.VITE_STYTCH_PUBLIC_TOKEN;
	private readonly stytchSubmitUrl = import.meta.env.VITE_STYTCH_SUBMIT_URL;

	async getPhoneNumberAsync (...args: Parameters<typeof UserFingerprintApi.getPhoneNumber>) {
		const { data } = await UserFingerprintApi.getPhoneNumber(...args);
		return data;
	}

	async validateAsync (...args: Parameters<typeof UserFingerprintApi.validate>) {
		const { data } = await UserFingerprintApi.validate(...args);
		return data;
	}

	async resendCodeAsync (...args: Parameters<typeof UserFingerprintApi.resendCode>) {
		const { data } = await UserFingerprintApi.resendCode(...args);
		return data;
	}

	async resendSmsCodeAsync (...args: Parameters<typeof UserFingerprintApi.resendSmsCode>) {
		const { data } = await UserFingerprintApi.resendSmsCode(...args);
		return data;
	}

	async getStytchTelemetryAsync (): Promise<string | null> {
		try {
			// Check if GetTelemetryID is available (script loaded)
			if (typeof GetTelemetryID === 'undefined') {
				return null;
			}

			// Generate telemetry ID using Stytch global function
			const config = {
				submitURL: this.stytchSubmitUrl,
				publicToken: this.stytchPublicToken,
			};
			const telemetryId = await GetTelemetryID(config);
			return telemetryId;
		}
		catch (error) {
			return null;
		}
	}

	async getThumbmarkFingerprintAsync (): Promise<string | null> {
		try {
			const thumbmark = new Thumbmark();
			const fingerprint = await thumbmark.get();

			if (!fingerprint || !fingerprint.thumbmark) {
				return null;
			}

			return fingerprint.thumbmark;
		}
		catch (error) {
			return null;
		}
	}

	private async callFingerprintAsync () {
		Sentry.init(); // prevents FingerPrint error

		// Initialize the agent at application startup.
		const fpPromise = FingerprintJS.load({
			apiKey: '6ApraYr3voVWasSebAVs',
			scriptUrlPattern: [
				'https://metrics.myarmado.fr/wc2gna8rrf/v4vx2g7r0r?apiKey=<apiKey>&version=<version>&loaderVersion=<loaderVersion>',
				FingerprintJS.defaultScriptUrlPattern, // Fallback to default CDN in case of error
			],
			endpoint: [
				'https://metrics.myarmado.fr/wc2gna8rrf/8xu2a62euk?region=eu',
				FingerprintJS.defaultEndpoint, // Fallback to default endpoint in case of error
			],
		});

		const fp = await fpPromise;
		const res = await fp.get({ extendedResult: true });

		const expirationDate = new Date();
		expirationDate.setDate(expirationDate.getDate() + 30);
		(res as any).expirationDate = expirationDate;

		localStorage.setItem('fpLocal', JSON.stringify(res));

		initSentry(getArmadoApp(), router);
	}

	async getFingerprintAsync (): Promise<ExtendedVisitorData> {
		const [fingerprintData, stytchTelemetryId, thumbmarkFingerprint] = await Promise.allSettled([
			this.getFingerprintJsDataAsync(),
			this.getStytchTelemetryAsync(),
			this.getThumbmarkFingerprintAsync(),
		]);

		// Handle FingerprintJS result
		const fpResult = fingerprintData.status === 'fulfilled' ? fingerprintData.value : null;
		// Handle Stytch result
		const stytchResult = stytchTelemetryId.status === 'fulfilled' ? stytchTelemetryId.value : null;
		// Handle Thumbmark result
		const thumbmarkResult = thumbmarkFingerprint.status === 'fulfilled' ? thumbmarkFingerprint.value : null;

		return {
			visitorId: fpResult?.visitorId || '',
			ipAddress: fpResult?.ipAddress || '',
			place: fpResult?.place || '',
			browserName: fpResult?.browserName || '',
			browserMajorVersion: fpResult?.browserMajorVersion || '',
			os: fpResult?.os || '',
			osVersion: fpResult?.osVersion || '',
			device: fpResult?.device || '',
			stytchTelemetryId: stytchResult || undefined,
			thumbmarkFingerprint: thumbmarkResult || undefined,
		};
	}

	private async getFingerprintJsDataAsync (): Promise<VisitorData> {
		let fpLocal = localStorage.getItem('fpLocal');
		const parsedFpLocal = JSON.parse(fpLocal ?? '{}');

		if (fpLocal) {
			if (Date.now() > new Date(parsedFpLocal.expirationDate).valueOf()) {
				fpLocal = null;
			}
		}

		if (fpLocal === null || !parsedFpLocal.visitorId) {
			await this.callFingerprintAsync();
			fpLocal = localStorage.getItem('fpLocal');
		}

		const visitorData = JSON.parse(fpLocal as string) as VisitorData;

		return {
			visitorId: visitorData?.visitorId,
			ipAddress: visitorData?.ip,
			place: `${visitorData?.ipLocation?.country?.name} ${visitorData?.ipLocation?.city?.name}`,
			browserName: visitorData?.browserName,
			browserMajorVersion: visitorData?.browserMajorVersion,
			os: visitorData?.os,
			osVersion: visitorData?.osVersion,
			device: visitorData?.device,
		};
	}

}

const fingerprintServiceSingleton = new FingerprintService();

export default function useFingerprintService () {
	return fingerprintServiceSingleton;
}
